import { EmbeddableWorkbookSubplaceWithoutRouter } from '@tableau/catalog-ui-temp';
import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { ISanitizer } from '~commonInfra/utils/Sanitize';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { getTelemetryCommonProperties } from '~src/catalog/DataCatalogTelemetry';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { LocationTypes } from '~src/personalSpace/model/ILocationInfo';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '~src/spacesPlatform/components/spaceComponents/SpaceTelemetry';
import { explorerSubspace, newExtractRefreshButton } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { ISpaceInfoTypeItemCount } from '~src/spacesPlatform/state/ContentCountsState';
import { SpaceInfo, SubspaceName, WorkbookSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';
import { ViewDefinition } from '~src/views/contentDefinitions/ViewDefinition';

import { CatalogApiRequests } from '../../catalog/CatalogApiRequests';
import { catalogApolloClient } from '../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { DataCatalogUIActionsService } from '../../catalog/services/DataCatalogUIActionsService';
import { Messages } from '../../codegen/Localize';
import { DatasourceDefinition } from '../../datasource/components/contentDefinitions/DatasourceDefinition';
import { DraftWorkbookSpaceProps } from '../../drafts/types/DraftsTypes';
import { ExtractTaskDefinition } from '../../extractTask/components/spaces/contentDefinitions/ExtractTaskDefinition';
import { FilterFieldNames } from '../../spacesPlatform/models/IFilterClause';
import { SubscriptionDefinition } from '../../subscription/contentDefinitions/SubscriptionDefinition';
import { CustomViewDefinition } from '../../views/contentDefinitions/customViews/CustomViewDefinition';
import { IWorkbookDetailInfoWithUsage } from '../models/IWorkbookInfo';
import { IWorkbookActions } from '../ServerApi/types/IWorkbookActions';
import { WorkbookSpaceHeader } from './WorkbookSpaceHeader';

export interface WorkbookSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly urls: SpacesSiteUrls;
  readonly catalogingOnSiteEnabled: boolean;
  readonly contentCounts: ISpaceInfoTypeItemCount<'workbook'>;
  readonly contentCountsUptoDate: boolean;
  readonly areUsersVisible: boolean;
  readonly draft?: DraftWorkbookSpaceProps;
}

export interface WorkbookSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
}

export interface WorkbookSpaceOwnProps {
  readonly workbookDetail: IWorkbookDetailInfoWithUsage;
  readonly workbookActions: IWorkbookActions;
  readonly workbookLocation: string;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly sanitizer: ISanitizer;
}

export type WorkbookSpaceProps = WorkbookSpaceStateProps & WorkbookSpaceOwnProps & WorkbookSpaceDispatchProps;

function lineageSubspace(
  workbook: IWorkbookDetailInfoWithUsage,
  siteUrls: SpacesSiteUrls,
  areUsersVisible: boolean,
  siteLuid: string,
): Subspace {
  return {
    name: 'lineage',
    tabLabel: Messages.dataCatalogLineage(),
    shouldRenderTabContentCount: false,
    renderContent: () =>
      <EmbeddableWorkbookSubplaceWithoutRouter
        vizportalUrlId={workbook.id}
        client={catalogApolloClient}
        siteUrls={siteUrls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        areUsersVisible={areUsersVisible}
        featureFlags={catalogFeatureFlagsAdapter()}
        telemetryContext={getTelemetryCommonProperties()}
        siteLuid={siteLuid}
      />
  };
}

export const getWorkbookUrl = (urls: SpacesSiteUrls, workbookId: string, subspace?: WorkbookSubspaceName) => {
  if (subspace) {
    const WorkbookSubspacesToUrl: Record<WorkbookSubspaceName, string> = {
      views: urls.workbookViewsUrl(workbookId),
      datasources: urls.workbookDatasourcesUrl(workbookId),
      dataconnections: (urls.workbookDatasourcesUrl(workbookId) + '?' + FilterFieldNames.contentType + '=' + 'dataconnection'),
      connectedMetrics: urls.workbookConnectedMetricsUrl(workbookId),
      customViews: urls.workbookCustomViewsUrl(workbookId),
      extractTasks: urls.workbookExtractRefreshesUrl(workbookId),
      subscriptions: urls.workbookSubscriptionsUrl(workbookId),
      lineage: urls.workbookLineageUrl(workbookId),
      usage: urls.workbookUsageUrl(workbookId),
    };

    const url = WorkbookSubspacesToUrl[subspace];
    if (url) {
      return url;
    }
  }

  return urls.workbookUrl(workbookId);
};

function getWorkbookSubspaces(
  workbook: IWorkbookDetailInfoWithUsage,
  workbookActions: IWorkbookActions,
  contentCounts: ISpaceInfoTypeItemCount<'workbook'>,
  contentCountsUpToDate: boolean,
  siteUrls: SpacesSiteUrls,
  catalogingOnSiteEnabled: boolean,
  areUsersVisible: boolean,
  usageSubspaceEnabled: boolean
): ReadonlyArray<Subspace> {

  // When User Visibility is limited and the user does not have permissions to view other
  //  users, Workbook's owner shows up as Hidden User. Under the same conditions, we need
  //  to disable subscriptions tab, because user can't see any subscriptions in the workbook
  //  space.
  let showSubscriptionsTab: boolean = !workbook.owner.isHiddenInternalUser;
  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());

  return _.compact<_.List<Subspace | false>>([
    explorerSubspace({
      name: 'views',
      tabLabel: Messages.views(),
      supportedDisplayModes: ViewDefinition.supportedDisplayModes,
      contentCount: contentCounts && contentCounts['views'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled'
    }),
    explorerSubspace({
      name: 'datasources',
      // TFSID:855453 This string has correct capitalization and avoids adding new string past loc change for next release (19.2)
      tabLabel: Messages.publishWorkbookSuccessDialogText_datasourcesHeader(),
      supportedDisplayModes: DatasourceDefinition.supportedDisplayModes,
      contentCount: contentCounts && contentCounts['datasources'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.workbooksDatasourcesEmptyMessage()
      }
    }),
    explorerSubspace({
      name: 'customViews',
      tabLabel: Messages.customViews(),
      supportedDisplayModes: CustomViewDefinition.supportedDisplayModes,
      contentCount: contentCounts && contentCounts['customViews'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.workbooksCustomViewsEmptyMessage()
      }
    }),
    workbook.hasExtracts && explorerSubspace({
      name: 'extractTasks',
      tabLabel: Messages.extractTasks(),
      supportedDisplayModes: ExtractTaskDefinition.supportedDisplayModes,
      contentCount: contentCounts && contentCounts['extractTasks'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarContentSpecificControls: {
        beginning: newExtractRefreshButton(workbook, workbookActions)
      },
      toolbarVisibility: 'enabled'
    }),
    showSubscriptionsTab && explorerSubspace({
      name: 'subscriptions',
      tabLabel: Messages.subscriptions(),
      supportedDisplayModes: SubscriptionDefinition.supportedDisplayModes,
      contentCount: contentCounts && contentCounts['subscriptions'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.workbooksSubscriptionsEmptyMessage()
      }
    }),
    catalogingOnSiteEnabled && lineageSubspace(
      workbook,
      siteUrls,
      areUsersVisible,
      siteLuid,
    ),
    usageSubspaceEnabled && explorerSubspace({
      name: 'usage',
      tabLabel: Messages.workbookUsage(),
      shouldRenderContentCount: false,
      toolbarVisibility: 'hidden',
      hideSelection: true,
      supportedDisplayModes: ['thumbnail'],
      supportFilterPanel: false,
      emptyStateProps: {
        primaryMessage: Messages.workbookUsageEmptyMessage()
      }
    }),
  ]);
}

export class WorkbookSpace extends React.PureComponent<WorkbookSpaceProps> {

  private memoizedGetWorkbookSubspaces = defaultMemoize(getWorkbookSubspaces);

  public componentDidUpdate(prevProps: WorkbookSpaceProps): void {
    this.fetchUpdatedCounts();
    if (prevProps.workbookDetail.id !== this.props.workbookDetail.id) {
      maybeSendTelemetry_favoriteItemOpened(this.props.workbookDetail);
    }
  }

  public componentDidMount(): void {
    this.fetchUpdatedCounts();
    maybeSendTelemetry_favoriteItemOpened(this.props.workbookDetail);
  }

  public exploreWorkbookUrl = (subspaceName?: WorkbookSubspaceName) => {
    return getWorkbookUrl(this.props.urls, this.props.workbookDetail.id, subspaceName);
  };

  public render() {
    const subspaces = this.memoizedGetWorkbookSubspaces(
      this.props.workbookDetail,
      this.props.workbookActions,
      this.props.contentCounts,
      this.props.contentCountsUptoDate,
      this.props.urls,
      this.props.catalogingOnSiteEnabled,
      this.props.areUsersVisible,
      FeatureFlags.authorMetricsEnabled && FeatureFlags.metricsSpaceEnabled && this.props.workbookLocation !== LocationTypes.PersonalSpace
    );

    const rawSubspaceName = this.props.spaceInfo.subspaceName;
    const subspaceTabName: SubspaceName = rawSubspaceName === 'dataconnections' ? 'datasources' : rawSubspaceName;

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={subspaceTabName}
        urlForSubspace={this.exploreWorkbookUrl}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'workbook'}
        spaceItemName={this.props.workbookDetail.name}
      />
    );
  }

  private mastheadElement(): React.ReactNode {
    const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());
    return (
      <WorkbookSpaceHeader
        item={this.props.workbookDetail}
        availableActions={this.props.workbookActions}
        openDetail={this.props.openDetail}
        addToFavorites={this.props.addToFavorites}
        removeFromFavorites={this.props.removeFromFavorites}
        sanitizer={this.props.sanitizer}
        urls={this.props.urls}
        catalogingOnSiteEnabled={this.props.catalogingOnSiteEnabled}
        draft={this.props.draft}
      />
    );
  }

  private fetchUpdatedCounts = (): void => {
    if (this.props.fetchUpdatedContentCounts) {
      this.props.fetchUpdatedContentCounts();
    }
  };
}
